export const APIFetchNoAuth = <T extends Record<string, any>>(
  backend: string
): ((url: string, reqInit: RequestInit) => Promise<T>) => {
  return baseFetch(backend);
};

export const AuthorizedFetch = <T extends Record<string, any>>(
  backendApi: string,
  token: string
): ((url: string, reqInit: RequestInit) => Promise<T>) => {
  return baseFetch<T>(backendApi, (r) => {
    r.headers = {
      ...r.headers,
      Authorization: `Bearer ${token}`,
    };
  });
};

const baseFetch = <T extends Record<string, any>>(
  backendApi: string,
  modifyReq?: (r: RequestInit) => void
): ((url: string, reqInit: RequestInit) => Promise<T>) => {
  return async (url: string, reqInit: RequestInit) => {
    if (modifyReq) {
      modifyReq(reqInit);
    }

    const res = await fetch(`${backendApi}${url}`, reqInit);

    if (!res.ok) {
      let errorMessage;
      // Read the body ONCE as text. This consumes the stream.
      const errorBodyAsText = await res.text();
      try {
        // Attempt to parse the retrieved text as JSON.
        const errorData = JSON.parse(errorBodyAsText);
        // Check for common error fields in JSON, fall back to the full text if not found or if parsing failed.
        errorMessage = errorData.error || errorData.message || errorBodyAsText;
      } catch (e) {
        // If JSON.parse fails, it means the body was not JSON. Use the raw text.
        errorMessage = errorBodyAsText;
      }
      throw new AuthFetchHTTPError(res.status, errorMessage);
    }

    const json = await res.json();
    return json as T;
  };
};

type ErrorMessage = { cause: string; messages: string[]; tags: Record<string, string | number>[]; grpcCode: number };

export class AuthFetchHTTPError extends Error {
  public readonly status: number;
  public readonly tags: Record<string, string | number> | null = null;
  public cause: string | null = null;
  public readonly grpcCode: number | null = null;

  constructor(status: number, message: string) {
    super(`HTTP error ${status}: ${message}`);
    this.name = 'AuthFetchHTTPError';
    this.status = status;

    let parsedMessage: ErrorMessage | null = null;
    try {
      parsedMessage = JSON.parse(message);
      if (parsedMessage?.tags) {
        this.tags = parsedMessage.tags[0]; // in practice there is only ever one tags object
      }
      if (parsedMessage?.grpcCode) {
        this.grpcCode = parsedMessage.grpcCode;
      }
    } catch (error) {
      // message is not JSON, leave cause and tags as null
    }
  }
}
